import {
  doc, getDoc, getDocFromServer, serverTimestamp, setDoc, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { safeSessionGet } from '../browserStorage';
import { FEATURE_FLAGS } from '../featureFlags';
import { normalizeProfileDetailsDraft } from '../profile/profileDetailsModel';
import {
  DEFAULT_FOLLOW_PROFILE_VISIBILITY,
  DEFAULT_ROUTINE_VISIBILITY,
  buildPublicProfilePayload,
} from '../follow/followModel';
import { cachedRead, invalidateCachedRead, setCachedRead, userProfileCacheKey } from './readCache';
const USER_PROFILE_TTL_MS = 5 * 60 * 1000;

function readUserProfileDocument(uid) {
  return cachedRead(userProfileCacheKey(uid), async () => {
    const snap = await getDoc(doc(db, 'users', uid));
    return snap.exists() ? snap.data() : null;
  }, USER_PROFILE_TTL_MS);
}

function userProfileFromAuth(user, data = {}) {
  return {
    ...data,
    uid: user.uid,
    email: data.email || user.email || '',
    displayName: data.displayName || user.displayName || '',
    avatarEmoji: data.avatarEmoji || '✨',
    avatarColor: data.avatarColor || '#FFD700',
  };
}

async function ensureDefaultPublicFollowProfile(user, data = {}) {
  if (!FEATURE_FLAGS.FOLLOW_FEATURE_ENABLED || !user?.uid) return false;
  const existingFollowProfile = data.followProfile || {};
  if (existingFollowProfile.profileVisibility) return false;

  const followProfile = {
    ...existingFollowProfile,
    bio: existingFollowProfile.bio || '',
    profileVisibility: DEFAULT_FOLLOW_PROFILE_VISIBILITY,
    routineDefaultVisibility: existingFollowProfile.routineDefaultVisibility || DEFAULT_ROUTINE_VISIBILITY,
    followersCount: existingFollowProfile.followersCount || 0,
    followingCount: existingFollowProfile.followingCount || 0,
    updatedAt: serverTimestamp(),
  };
  const profile = userProfileFromAuth(user, {
    ...data,
    followProfile,
  });
  const publicProfile = buildPublicProfilePayload(user.uid, profile, followProfile);

  await Promise.all([
    setDoc(doc(db, 'users', user.uid), { followProfile }, { merge: true }),
    setDoc(doc(db, 'publicProfiles', user.uid), {
      ...publicProfile,
      updatedAt: serverTimestamp(),
    }, { merge: true }),
  ]);
  return true;
}

export async function createUserIfNew(user) {
  const ref  = doc(db, 'users', user.uid);
  const data = await readUserProfileDocument(user.uid);
  if (!data) {
    const pendingReferralUid = safeSessionGet('pendingReferralUid');
    const appReferredBy = pendingReferralUid && pendingReferralUid !== user.uid
      ? pendingReferralUid
      : '';
    const followProfile = FEATURE_FLAGS.FOLLOW_FEATURE_ENABLED ? {
      bio: '',
      profileVisibility: DEFAULT_FOLLOW_PROFILE_VISIBILITY,
      routineDefaultVisibility: DEFAULT_ROUTINE_VISIBILITY,
      followersCount: 0,
      followingCount: 0,
      updatedAt: serverTimestamp(),
    } : undefined;
    const userProfile = {
      uid:         user.uid,
      email:       user.email,
      displayName: user.displayName || '',
      createdAt:   serverTimestamp(),
      appReferredBy,
      avatarEmoji: '✨',
      avatarColor: '#FFD700',
      instagramHandle: '',
      entitlements: {
        pro: { active: false, source: 'none' },
      },
      ...(followProfile ? { followProfile } : {}),
    };
    await setDoc(ref, userProfile, { merge: true });
    setCachedRead(userProfileCacheKey(user.uid), userProfile, USER_PROFILE_TTL_MS);
    if (followProfile) {
      await setDoc(doc(db, 'publicProfiles', user.uid), {
        ...buildPublicProfilePayload(user.uid, userProfile, followProfile),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }
    return;
  }
  setCachedRead(userProfileCacheKey(user.uid), userProfileFromAuth(user, data), USER_PROFILE_TTL_MS);
  const followProfileUpdated = await ensureDefaultPublicFollowProfile(user, data);
  if (followProfileUpdated) {
    invalidateCachedRead(userProfileCacheKey(user.uid));
  }

  if (!data.onboardingDone && !data.onboarding && (data.joinedChallengeIds?.length > 0 || data.stats?.challengesJoined > 0)) {
    await setDoc(ref, { onboardingDone: true }, { merge: true });
    invalidateCachedRead(userProfileCacheKey(user.uid));
  }

  const flatJoined = data['stats.challengesJoined'];
  const flatOwned  = data['stats.challengesOwned'];
  if ((flatJoined != null || flatOwned != null) && !data.stats?.challengesJoined) {
    await updateDoc(ref, {
      'stats.challengesJoined': flatJoined || 0,
      'stats.challengesOwned':  flatOwned  || 0,
    });
    invalidateCachedRead(userProfileCacheKey(user.uid));
  }
}

export async function saveOnboarding(uid, answers) {
  const ref = doc(db, 'users', uid);
  await setDoc(ref, {
    onboarding: { ...answers, completedAt: serverTimestamp() },
    onboardingDone: true,
  }, { merge: true });
  invalidateCachedRead(userProfileCacheKey(uid));
}

export async function saveProfileDetails(uid, details) {
  const normalized = normalizeProfileDetailsDraft(details);
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : {};
  const existingOnboarding = existing.onboarding || {};
  const onboarding = {
    ...existingOnboarding,
    ...normalized,
    completedAt: existingOnboarding.completedAt || serverTimestamp(),
    profileDetailsUpdatedAt: serverTimestamp(),
  };

  await setDoc(ref, {
    firstName: normalized.firstName,
    onboarding,
    onboardingDone: true,
  }, { merge: true });
  invalidateCachedRead(userProfileCacheKey(uid));
  return {
    firstName: normalized.firstName,
    onboarding,
  };
}

export async function getUserProfile(uid) {
  return readUserProfileDocument(uid);
}

export async function refreshUserProfile(uid) {
  const snap = await getDocFromServer(doc(db, 'users', uid));
  const profile = snap.exists() ? snap.data() : null;
  setCachedRead(userProfileCacheKey(uid), profile, USER_PROFILE_TTL_MS);
  return profile;
}
